import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { encrypt, decrypt } from './encryption';

export const storeApiKey = async (userId: string, apiKey: string, licenseKey: string) => {
  if (!userId || !apiKey || !licenseKey) {
    throw new Error('User ID, API key, and license key are required');
  }

  try {
    console.log('Storing API key for user:', userId);

    // Encrypt the API key using the user's LemonSqueezy subscription ID (license key)
    const encryptedApiKey = encrypt(apiKey, licenseKey);

    // Store the encrypted API key in the user's document
    const docRef = doc(db, 'users', userId);
    await setDoc(docRef, {
      apiKey: encryptedApiKey,
      apiKeyUpdatedAt: serverTimestamp()
    }, { merge: true }); // Use merge to not overwrite other user fields

    console.log('API key stored successfully');
    return true;
  } catch (error) {
    console.error('Error storing API key:', error);
    throw error;
  }
};

/**
 * Returns the decrypted API key, or null when the user genuinely has no key stored.
 * Throws on transient/exceptional failures (Firestore fetch or decryption errors) so
 * callers can distinguish "not configured" from "couldn't check right now".
 */
export const getStoredApiKey = async (userId: string, licenseKey: string): Promise<string | null> => {
  if (!userId || !licenseKey) {
    throw new Error('User ID and license key are required');
  }

  console.log('Getting stored API key for user:', userId);

  const docRef = doc(db, 'users', userId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    console.log('No user document found');
    return null;
  }

  const data = docSnap.data();
  if (!data.apiKey) {
    console.log('No API key found in user document');
    return null;
  }

  // Decrypt the API key using the user's LemonSqueezy subscription ID (license key)
  return decrypt(data.apiKey, licenseKey);
};

export const removeApiKey = async (userId: string) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    console.log('Removing API key for user:', userId);

    // Remove the apiKey field from the user document
    const docRef = doc(db, 'users', userId);
    await setDoc(docRef, {
      apiKey: null,
      apiKeyUpdatedAt: serverTimestamp()
    }, { merge: true });

    console.log('API key removed successfully');
    return true;
  } catch (error) {
    console.error('Error removing API key:', error);
    throw error;
  }
};
